//@flow
import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { connect } from 'react-redux';

import {
  backup,
  getAdminProperties,
  restore,
  setBackupConfig,
} from 'state/commands/adminPropertiesCommands';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import {
  getActivePanelId,
  getBackupConfig,
  getServerStatus,
} from 'state/selectors';
import type { BackupConfiguration, Dispatch, ServerStatus } from 'state/types';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import {
  BROWSE_SERVER_FOLDERS_PANEL_ID,
  SERVER_STATE_STOPPED,
} from 'util/constants';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import BrowseServerFoldersDialog from 'view/App/DialogPanels/BrowseServerFoldersDialog';
import FormField from 'view/Forms/FormField';
import StaticTextField from 'view/Forms/StaticTextField';
import { FILETYPE } from 'portfolio-shared/util/folderUtils';

const loc = localizedStrings;

type Props = {
  activePanelId?: string,
  backup: typeof backup,
  backupConfig: BackupConfiguration,
  getActivePanelId: typeof getActivePanelId,
  getAdminProperties: typeof getAdminProperties,
  intl: IntlShape,
  restore: typeof restore,
  setBackupConfig: typeof setBackupConfig,
  serverStatus: ServerStatus,
  showDialogPanel: typeof showDialogPanel,
};

type State = {
  isSelectingRestoreArchive: boolean,
};

const backupDays = [
  { label: loc.BACKUP_SUNDAY_LABEL, value: 'areBackupsScheduledSunday' },
  { label: loc.BACKUP_MONDAY_LABEL, value: 'areBackupsScheduledMonday' },
  { label: loc.BACKUP_TUESDAY_LABEL, value: 'areBackupsScheduledTuesday' },
  { label: loc.BACKUP_WEDNESDAY_LABEL, value: 'areBackupsScheduledWednesday' },
  { label: loc.BACKUP_THURSDAY_LABEL, value: 'areBackupsScheduledThursday' },
  { label: loc.BACKUP_FRIDAY_LABEL, value: 'areBackupsScheduledFriday' },
  { label: loc.BACKUP_SATURDAY_LABEL, value: 'areBackupsScheduledSaturday' },
];

const retainOptions = ['1', '2', '3', '5', '10'];

const TIME_SEP = ':';
const AM = 'AM';
const PM = 'PM';
const DEFAULT_TIME = '12:00:AM';

class BackupsPage extends Component<Props, State> {
  getTimeOptionForValue: Function;
  getTimeOptions: Function;
  handleBrowseForDirectory: Function;
  handleClickBackup: Function;
  handleClickRestore: Function;
  handlePathSelected: Function;
  normalizeTimeValueForServer: Function;
  onChangeCheckbox: Function;
  onChangeField: Function;
  setIsScheduled: Function;
  submit: Function;

  state = {
    isSelectingRestoreArchive: false,
  };

  constructor() {
    super();
    this.getTimeOptionForValue = this.getTimeOptionForValue.bind(this);
    this.getTimeOptions = this.getTimeOptions.bind(this);
    this.handleBrowseForDirectory = this.handleBrowseForDirectory.bind(this);
    this.handleClickBackup = this.handleClickBackup.bind(this);
    this.handleClickRestore = this.handleClickRestore.bind(this);
    this.handlePathSelected = this.handlePathSelected.bind(this);
    this.normalizeTimeValueForServer = this.normalizeTimeValueForServer.bind(
      this
    );
    this.onChangeCheckbox = this.onChangeCheckbox.bind(this);
    this.onChangeField = this.onChangeField.bind(this);
    this.setIsScheduled = this.setIsScheduled.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    this.props.getAdminProperties([ADMIN_PROPERTIES.backupConfig]);
  }

  handleBrowseForDirectory() {
    this.setState({
      isSelectingRestoreArchive: false,
    });
    this.props.showDialogPanel(BROWSE_SERVER_FOLDERS_PANEL_ID);
  }

  handleBrowseForZipFile() {
    this.setState({
      isSelectingRestoreArchive: true,
    });
    this.props.showDialogPanel(BROWSE_SERVER_FOLDERS_PANEL_ID);
  }

  handleClickBackup() {
    this.props.backup();
  }

  handleClickRestore() {
    this.handleBrowseForZipFile();
  }

  handlePathSelected(path) {
    if (this.state.isSelectingRestoreArchive) {
      this.props.restore(path);
    } else {
      this.submit('backupsDirectory', path);
    }
  }

  onChangeField(event, fieldName) {
    this.submit(fieldName, event.target.value);
  }

  onChangeCheckbox(event, fieldName) {
    this.submit(fieldName, event.target.checked);
  }

  normalizeTimeValueForServer(config: BackupConfiguration) {
    // don't ever send the server null for time
    if (!config.backupScheduledTime) {
      config.backupScheduledTime = DEFAULT_TIME;
    }
    const parts: string[] = config.backupScheduledTime.split(TIME_SEP);
    // server can send hour as '00' but it expects to be given '12'
    if (Number(parts[0]) === 0) {
      config.backupScheduledTime =
        '12' + TIME_SEP + parts[1] + TIME_SEP + parts[2];
    }
  }

  setIsScheduled(c: BackupConfiguration) {
    c.areBackupsScheduled =
      c.areBackupsScheduledSunday ||
      c.areBackupsScheduledMonday ||
      c.areBackupsScheduledTuesday ||
      c.areBackupsScheduledWednesday ||
      c.areBackupsScheduledThursday ||
      c.areBackupsScheduledFriday ||
      c.areBackupsScheduledSaturday;
  }

  submit(fieldName, value) {
    const newConfig = {
      ...this.props.backupConfig,
      [fieldName]: value,
    };
    this.normalizeTimeValueForServer(newConfig);
    this.setIsScheduled(newConfig);
    this.props.setBackupConfig(newConfig);
  }

  // 48 options, 1 per each half hour
  getTimeOptions() {
    let opts = [];
    for (let ix = 0; ix < 48; ++ix) {
      let hour = Math.floor(ix / 2) || 12;
      hour = hour > 12 ? hour - 12 : hour;
      const min = ix % 2 === 0 ? '00' : '30';
      const ampm = ix < 24 ? AM : PM;
      opts.push({
        label: hour + TIME_SEP + min + ' ' + ampm,
        value: hour + TIME_SEP + min + TIME_SEP + ampm,
      });
    }
    return opts;
  }

  getTimeOptionForValue(value) {
    let index = 0;
    if (value) {
      const parts: string[] = value.split(TIME_SEP);
      const hours = Number(parts[0]);
      const mins = Number(parts[1]);
      index = hours * 2 + (mins >= 30 ? 1 : 0) + (parts[2] === AM ? 0 : 24);
    }
    return this.getTimeOptions()[index];
  }

  render() {
    const {
      activePanelId,
      backupConfig,
      intl: { formatDate, formatMessage, formatTime },
      serverStatus,
    } = this.props;
    const backupDate = backupConfig.nextScheduledBackup;
    const formattedDate =
      (backupDate && formatDate(backupDate)) ||
      formatMessage(localizedStrings.DEFAULT_CATALOG_NONE);
    const formattedTime = (backupDate && formatTime(backupDate)) || '';
    const timeOptionForValue = this.getTimeOptionForValue(
      backupConfig.backupScheduledTime
    ).value;
    const isStopped = serverStatus.runState === SERVER_STATE_STOPPED;
    return (
      <div className="page" data-test-name="backup-page">
        <header className="page-header">
          <FormattedMessage {...loc.NAV_BACKUPS} />
        </header>
        <div className="page-contents-textured portfolio-form-container">
          <div className="box">
            <p>
              <FormattedMessage {...loc.BACKUP_EXPLANATION_TEXT_1} />
            </p>
            <p>
              <FormattedMessage {...loc.BACKUP_EXPLANATION_TEXT_2} />
            </p>

            <div className="vertical-spacer-large" />

            <div className="buttons large-buttons backups-page-main-button-group">
              <button
                className="button is-medium is-primary is-outlined"
                data-test-name="backup-button"
                onClick={this.handleClickBackup}
              >
                <FormattedMessage {...loc.BACKUP_BACKUP_NOW_BUTTON} />
              </button>

              <div className="horizontal-spacer-xlarge" />

              <button
                className="button is-medium is-primary is-outlined"
                data-test-name="restore-button"
                onClick={this.handleClickRestore}
              >
                <FormattedMessage {...loc.BACKUP_RESTORE_BUTTON} />
              </button>
            </div>
          </div>
          <div className="box">
            <StaticTextField
              dataTestName="next-backup"
              key="BACKUP_NEXT_SCHEDULED_LABEL"
              label={formatMessage(loc.BACKUP_NEXT_SCHEDULED_LABEL)}
              value={`${formattedDate} ${formattedTime}`}
              valueClassName={
                !backupConfig.areBackupsScheduled ? 'has-text-danger' : ''
              }
            />

            <FormField label={formatMessage(loc.AT_TIME)}>
              <div className="select">
                <select
                  id="backupScheduleTime"
                  onChange={e => this.onChangeField(e, 'backupScheduledTime')}
                  style={{ width: '140px' }}
                  value={timeOptionForValue}
                >
                  {this.getTimeOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>

            <div className="field is-horizontal is-grouped">
              <div className="field-label is-normal">
                <div className="label">
                  <FormattedMessage {...loc.BACKUP_SCHEDULE_VALUE} />
                </div>
              </div>
              <div className="field-body">
                <div
                  className="field is-grouped is-grouped-multiline"
                  style={{ marginTop: '5px' }}
                >
                  {backupDays.map(backupDay => (
                    <div className="control" key={backupDay.value}>
                      <input
                        checked={!!backupConfig[backupDay.value]}
                        className="is-checkradio"
                        id={backupDay.value}
                        onChange={e =>
                          this.onChangeCheckbox(e, backupDay.value)
                        }
                        type="checkbox"
                      />
                      <label className="label" htmlFor={backupDay.value}>
                        <FormattedMessage {...backupDay.label} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="box">
            <StaticTextField
              dataTestName="most-recent-backup"
              key="BACKUP_MOST_RECENT_LABEL"
              label={formatMessage(loc.BACKUP_MOST_RECENT_LABEL)}
              value={
                backupConfig.mostRecentBackupFileName ||
                formatMessage(localizedStrings.DEFAULT_CATALOG_NONE)
              }
            />

            <div className="field is-horizontal has-addons">
              <div className="field-label is-normal">
                <label className="label">
                  <FormattedMessage {...loc.BACKUP_DIRECTORY_LABEL} />
                </label>
              </div>
              <div className="field-body">
                <div className="control">
                  <input
                    className="input"
                    data-test-name="store-in-directory"
                    readOnly
                    style={{ width: '600px' }}
                    value={backupConfig.backupsDirectory || ''}
                  />
                </div>
                <div className="control">
                  <button
                    className={`button is-primary is-outlined`}
                    data-test-name="browse-for-directory"
                    disabled={!isStopped}
                    onClick={this.handleBrowseForDirectory}
                  >
                    <FormattedMessage {...loc.BROWSE} />
                  </button>
                </div>
              </div>
            </div>
            {!isStopped && (
              <StaticTextField
                label=""
                valueClassName="help"
                value={formatMessage(
                  loc.BACKUP_CONFIGURATION_SERVER_RUNNING_MESSAGE
                )}
              />
            )}

            <div className="field is-horizontal has-addons">
              <div className="field-label is-normal">
                <div className="label">
                  <FormattedMessage {...loc.BACKUP_RETAIN_LABEL} />
                </div>
              </div>
              <div className="field-body">
                <div className="field has-addons">
                  <div className="select">
                    <select
                      className="select"
                      data-test-name="backups-to-retain"
                      id="backupsRetainCount"
                      onChange={e =>
                        this.onChangeField(e, 'backupsRetainCount')
                      }
                      style={{ width: '100px' }}
                      value={backupConfig.backupsRetainCount}
                    >
                      {retainOptions.map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-label is-normal">
                    <div
                      className="label is-pulled-left"
                      style={{ marginLeft: '16px' }}
                    >
                      <FormattedMessage {...loc.BACKUP_FILES_LABEL} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="vertical-spacer-large" />

            <div className="field has-addons">
              <div className="control">
                <input
                  checked={backupConfig.isEmailNotificationConfigured || false}
                  className="is-checkradio"
                  data-test-name="enable-email-notifications"
                  id="isEmailNotificationConfigured"
                  onChange={e =>
                    this.onChangeCheckbox(e, 'isEmailNotificationConfigured')
                  }
                  type="checkbox"
                />
                <label
                  className="label control"
                  htmlFor="isEmailNotificationConfigured"
                >
                  {formatMessage(loc.BACKUP_NOTIFICATIONS)}
                </label>
              </div>
              <div className="control">
                <a className="is-indented control" href="#globalSettings/email">
                  <FormattedMessage {...loc.CONFIGURE_EMAIL_LINK} />
                </a>
              </div>
            </div>
          </div>
        </div>
        {activePanelId === BROWSE_SERVER_FOLDERS_PANEL_ID && (
          <BrowseServerFoldersDialog
            fileFilter={
              (this.state.isSelectingRestoreArchive && zipFileFilter) ||
              undefined
            }
            onPathSelected={this.handlePathSelected}
            showFiles={this.state.isSelectingRestoreArchive}
          />
        )}
      </div>
    );
  }
}

function zipFileFilter(tree) {
  return (
    tree &&
    tree.fullPath &&
    (tree.type === FILETYPE.directory ||
      (tree.type === FILETYPE.file &&
        tree.fullPath.toLowerCase().endsWith('.zip')))
  );
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    backupConfig: getBackupConfig(state),
    serverStatus: getServerStatus(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      backup,
      getAdminProperties,
      restore,
      setBackupConfig,
      showDialogPanel,
    })
)(injectIntl(BackupsPage));



// WEBPACK FOOTER //
// ./src/view/Pages/BackupsPage.js